<template>
    <div class="vp-menu-anchor">
        <button
            type="button"
            class="vp-icon-button"
            data-vp-quality-toggle
            :aria-label="`${t('Settings')} — ${t('Quality')}: ${selectedLabel}`"
            :aria-expanded="open"
            @click="open = !open"
        >
            <VideoIcon name="settings" />
        </button>

        <div v-if="open" ref="panelEl" class="vp-panel vp-quality-menu" role="menu">
            <div class="vp-label vp-quality-title">{{ t("Quality") }}</div>
            <button
                v-for="option in options"
                :key="option.index"
                type="button"
                role="menuitemradio"
                class="vp-quality-option"
                :aria-checked="option.index === selected"
                @click="select(option.index)"
            >
                {{ option.label }}
            </button>
        </div>
    </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from "vue"
import VideoIcon from "./VideoIcon.vue"
import { useCoreI18n } from "../useCoreI18n.js"

const props = defineProps({
    levels: { type: Array, default: () => [] },
    selected: { type: Number, default: -1 },
})

const emit = defineEmits(["select"])

const t = useCoreI18n()
const open = ref(false)
const panelEl = ref(null)

const options = computed(() => [
    { index: -1, label: t("Auto") },
    ...[...props.levels].sort((a, b) => b.height - a.height).map((level) => ({ index: level.index, label: `${level.height}p` })),
])

const selectedLabel = computed(() => options.value.find((option) => option.index === props.selected)?.label ?? t("Auto"))

function select(index) {
    emit("select", index)
    open.value = false
}

// Same close behavior as VideoChapterMenu: touch devices have no
// mouseleave, so a pointerdown outside the panel (the toggle excluded, it
// closes the panel itself) or Escape closes it.
function onOutsidePointerDown(event) {
    const path = event.composedPath()

    if (panelEl.value && !path.includes(panelEl.value) && !path.some((node) => node.dataset?.vpQualityToggle !== undefined)) {
        open.value = false
    }
}

function onKeydown(event) {
    if (event.key === "Escape") {
        open.value = false
    }
}

function stopListening() {
    document.removeEventListener("pointerdown", onOutsidePointerDown, true)
    document.removeEventListener("keydown", onKeydown)
}

watch(open, (isOpen) => {
    stopListening()

    if (isOpen) {
        document.addEventListener("pointerdown", onOutsidePointerDown, true)
        document.addEventListener("keydown", onKeydown)
    }
})

onBeforeUnmount(stopListening)
</script>

<style>
.vp-menu-anchor {
    position: relative;
}

.vp-quality-menu {
    position: absolute;
    right: 0;
    bottom: 52px;
    z-index: 2;
    min-width: 160px;
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.vp-quality-title {
    padding: 6px 8px;
}

.vp-quality-option {
    padding: 8px;
    border-radius: 8px;
    text-align: left;
    font-size: 13px;
    color: var(--vp-muted);
    background: transparent;
}

.vp-quality-option:hover {
    background: rgba(251, 249, 245, 0.08);
}

.vp-quality-option[aria-checked="true"] {
    color: var(--vp-paper);
    box-shadow: inset 2px 0 0 var(--vp-accent);
}
</style>
